/**
 * @tradeview/core — the platform the modules plug into.
 *
 * Owns: the module contract (manifest schema), tenancy path resolution, the module
 * registry, and the entitlement check. Owns nothing a module should own.
 */
import { z } from 'zod';
import { Manifest as ManifestSchema, PhaseStates } from './manifest.schema.js';
import { ROLES, type Role } from '@tradeview/shared';

export { ManifestSchema, PhaseStates };
export { ENTITLEMENTS_DOC, withDefaults, startTrial, setModule } from './entitlements.js';
export type Manifest = z.infer<typeof ManifestSchema>;
export type ModuleState = (typeof PhaseStates)[number];

// ── Tenancy (roadmap §3.4, decision 2): path-based with a legacy resolver ───────
/** The company id that stands for Nextgen's v1 data at the Firestore root. */
export const LEGACY_COMPANY_ID = 'nextgen';

/** Resolve collection paths for a company. Legacy resolves to the root collections v1 uses. */
export function paths(companyId: string) {
  const root = companyId === LEGACY_COMPANY_ID ? '' : `companies/${companyId}/`;
  return {
    companyId,
    isLegacy: companyId === LEGACY_COMPANY_ID,
    company: companyId === LEGACY_COMPANY_ID ? 'settings/companyProfile' : `companies/${companyId}`,
    projects: `${root}projects`,
    project: (pid: string) => `${root}projects/${pid}`,
    projectSub: (pid: string, sub: string) => `${root}projects/${pid}/${sub}`,
    users: `${root}users`,
    timeEntries: `${root}timeEntries`,
    schedule: `${root}schedule`,
    settings: `${root}settings`,
    collection: (name: string) => `${root}${name}`,
  } as const;
}

// ── Entitlements ──────────────────────────────────────────────────────────────
export interface Entitlements {
  /** module id -> state. Missing = locked. */
  modules: Record<string, Exclude<ModuleState, 'empty'> | { state: 'trial'; endsAt: string }>;
}

export function moduleState(ent: Entitlements | null | undefined, id: string): ModuleState {
  const v = ent?.modules?.[id];
  if (!v) return 'locked';
  if (typeof v === 'string') return v;
  return v.state;
}

export function trialDaysLeft(ent: Entitlements | null | undefined, id: string, now = new Date()): number | null {
  const v = ent?.modules?.[id];
  if (!v || typeof v === 'string') return null;
  return Math.max(0, Math.ceil((new Date(v.endsAt).getTime() - now.getTime()) / 86_400_000));
}

// ── Registry ──────────────────────────────────────────────────────────────────
export interface RegisteredModule {
  manifest: Manifest;
  /** Lazy screen loader: export name -> component. Filled by the generated registry. */
  screens: Record<string, () => Promise<{ default: unknown }>>;
}

const registry = new Map<string, RegisteredModule>();

export function registerModule(m: RegisteredModule) {
  const parsed = ManifestSchema.parse(m.manifest);
  for (const r of parsed.roles) if (!(r in ROLES)) throw new Error(`module ${parsed.id}: unknown role ${r}`);
  registry.set(parsed.id, { ...m, manifest: parsed });
}

export function listModules(): RegisteredModule[] {
  return [...registry.values()].sort((a, b) => a.manifest.name.localeCompare(b.manifest.name));
}

export function getModule(id: string): RegisteredModule | undefined { return registry.get(id); }

/** Modules a person can open: role allowed, and the company has it active or on trial. */
export function visibleModules(role: Role | string | null, ent: Entitlements | null): RegisteredModule[] {
  return listModules().filter(m => {
    const st = moduleState(ent, m.manifest.id);
    return (st === 'active' || st === 'trial') && (!role || m.manifest.roles.includes(role));
  });
}

/** Dependencies that are not active/trial for this company, for the "Add X first?" prompt. */
export function missingDependencies(id: string, ent: Entitlements | null): string[] {
  const m = registry.get(id);
  if (!m) return [];
  return m.manifest.dependencies.filter(d => !['active', 'trial'].includes(moduleState(ent, d)));
}

/** Nav entries for one surface, from every visible module, in declared order. */
export function navFor(surface: 'phone' | 'desktop', role: Role | string | null, ent: Entitlements | null) {
  return visibleModules(role, ent)
    .flatMap(m => m.manifest.nav.filter(n => n.where.includes(surface)).map(n => ({ ...n, moduleId: m.manifest.id, bundle: m.manifest.bundle })))
    .sort((a, b) => a.order - b.order);
}
